import React, { useState } from 'react';
import styled from '@emotion/styled';
import ScrollLink from '../../common/ScrollLink';
import { theme } from '../../../styles/theme';
import { getNeatScheduleCopyrightYears } from '../../../utils/copyrightYear';
import NeatScheduleMark from '../../../../shared/components/brand/NeatScheduleMark';

const FooterContainer = styled.footer`
  background: ${theme.colors.background.secondary};
  padding: ${theme.spacing.xl} 0;
  border-top: 1px solid ${theme.colors.secondary.dark};
`;

const Content = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
`;

const BrandSection = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.secondary.dark};
`;

const Logo = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing.sm};
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  color: ${theme.colors.text.secondary};
  margin: 0 0 ${theme.spacing.lg};
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.5;
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.sm};
`;

const SocialIcon = styled.a`
  color: ${theme.colors.text.secondary};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover {
    color: ${theme.colors.primary.main};
    background-color: ${theme.colors.secondary.main};
  }

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }
`;

const AccordionSection = styled.div`
  margin-bottom: ${theme.spacing.sm};
`;

const AccordionHeader = styled.button`
  width: 100%;
  padding: ${theme.spacing.md};
  background: none;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  border-bottom: 1px solid ${theme.colors.secondary.dark};
  letter-spacing: 0.02em;
  text-transform: uppercase;

  svg {
    width: 18px;
    height: 18px;
    transition: transform 0.3s ease;
    transform: rotate(${props => props.isOpen ? '180deg' : '0deg'});
  }
`;

const AccordionContent = styled.div`
  padding: ${props => props.isOpen ? theme.spacing.md : '0'} ${theme.spacing.md};
  max-height: ${props => props.isOpen ? '200px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.a`
  color: ${theme.colors.text.secondary};
  text-decoration: none;
  font-size: ${theme.typography.fontSize.sm};
  padding: ${theme.spacing.sm} 0;
  display: block;
  transition: color 0.2s ease;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    color: ${theme.colors.primary.main};
  }
`;

const Copyright = styled.div`
  text-align: center;
  color: ${theme.colors.text.light};
  font-size: ${theme.typography.fontSize.xs};
  margin-top: ${theme.spacing.xl};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.secondary.dark};
`;

const Accordion = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <AccordionSection>
      <AccordionHeader onClick={() => setIsOpen(!isOpen)} isOpen={isOpen}>
        {title}
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </AccordionHeader>
      <AccordionContent isOpen={isOpen}>
        {children}
      </AccordionContent>
    </AccordionSection>
  );
};

const MobileFooter = () => {
  const handleScroll = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <FooterContainer>
      <Content>
        <BrandSection>
          <Logo>
            <NeatScheduleMark />
          </Logo>
          <Description>
            Making scheduling simple and efficient for businesses of all sizes.
          </Description>
          <SocialLinks>
            {/* Facebook icon */}
            <SocialIcon href="https://www.facebook.com/neatschedule" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
              </svg>
            </SocialIcon>
            {/* Twitter icon */}
            <SocialIcon
              href="https://x.com/neatschedule"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
            </SocialIcon>
            {/* Instagram icon (prepared but hidden for now) */}
            <SocialIcon
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'none' }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm10 2c1.654 0 3 1.346 3 3v10c0 1.654-1.346 3-3 3H7c-1.654 0-3-1.346-3-3V7c0-1.654 1.346-3 3-3h10zm-5 3a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6zm5.5-3a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"/>
              </svg>
            </SocialIcon>
          </SocialLinks>
        </BrandSection>

        <Accordion title="Product">
          <LinkList>
            <li><FooterLink as="button" onClick={() => handleScroll('features')}>Features</FooterLink></li>
            <li><FooterLink as="button" onClick={() => handleScroll('pricing')}>Pricing</FooterLink></li>
            <li><FooterLink as="button" onClick={() => handleScroll('comparison')}>Compare Plans</FooterLink></li>
          </LinkList>
        </Accordion>

        <Accordion title="Company">
          <LinkList>
            <li><FooterLink href="/about">About Us</FooterLink></li>
            <li><FooterLink href="/contact">Contact</FooterLink></li>
          </LinkList>
        </Accordion>

        <Accordion title="Legal">
          <LinkList>
            <li><FooterLink href="/main/privacy-policy">Privacy Policy</FooterLink></li>
            <li><FooterLink href="/main/cookie-policy">Cookie Policy</FooterLink></li>
            <li><FooterLink href="/main/terms">Terms of Service</FooterLink></li>
          </LinkList>
        </Accordion>

        <Copyright>
          <p>© {getNeatScheduleCopyrightYears()} Neat Schedule. All rights reserved.</p>
        </Copyright>
      </Content>
    </FooterContainer>
  );
};

export default MobileFooter; 